import { abGetDefaultCity } from '../ab/widgets-home';
import { YPaymentAmount } from '../y/amount';
import { $api } from '../y/api';
import { $i18n, yGetI18N, YI18NString } from '../y/i18n';
import { $time, YTime } from '../y/time';
import { YCountryId, YId, YTimeString } from '../y/types';
import { CHBookingListSession } from './c-booking-home';
import { c_cities, CCityId, UCoordinates } from './c-city';
import { CEventId } from './c-event';
import { CSessionId, CVideoId } from './c-event-admin';
import { c_event_tags, CEventSubquery, CEventTag, CEventTagId, cEventTagName } from './c-event-tag';
import { CProducerId, CTicketType } from './c-producer';
import { CVenue, CVenueId } from './c-venue';
import { UMediaLink } from './u-media-link';

export interface CHEventListSession {
    id: CSessionId;
    time: YTimeString;
    is_bookable: boolean;
    is_drop_in: boolean;
}

export interface CHEventList {
    id: CEventId;
    name: YI18NString;
    city_id: CCityId;
    venue_id: CVenueId;
    producer_id: CProducerId;
    slug: string;
    venue_name: string;
    event_tag_ids: CEventTagId[];
    sessions: CHEventListSession[];
    is_loyalty: boolean;
    rating_score?: number;
    picture_id: YId;
    popularity: number;
    dots: number;
    published_at: YTimeString;
    has_videos: boolean;

    //cached
    _search: string;
}

export function chEventListSessionTimeAfter(event: CHEventList, when: YTime) {
    let bookable: YTime | undefined;
    for(const s of event.sessions) {
        const t = $time(s.time);
        if(t.after(when))
            return t;
        else if(!bookable)
            bookable = t;
    }
    return bookable;
}

export function chEventListNextSessionTime(event: CHEventList, after?: YTime) {
    let bookable = event.sessions.filter(s => s.is_bookable);
    if(bookable.length) {
        if(after) {
            const filtered_bookable = bookable.filter(s => $time(s.time).after(after));
            if(filtered_bookable.length)
                bookable = filtered_bookable;
        }
        if(bookable.length > 1)
            return $i18n.tl.several_dates;
        const text = $time(bookable[0].time).formatIn(bookable[0].is_drop_in ? 'd MMM' : 'd MMM, HH:mm', 'Europe/Stockholm');
        return text;
    }

    // if(model.videos && model.videos.length && this.isVideoAvailable(model.videos[0])) {
    //     const time = $time(model.videos[0].date);
    //     if(time.is_future) {
    //         return $i18n.tl.t_video_starts + ' ' + time.format('d MMM, HH:mm');
    //     } else if(time.add(model.length, $time.minute).is_future) {
    //         return $i18n.tl.t_video_live_now;
    //     }
    // }

    return;
}

export function chEventListFilterActive(e: CHEventList) {
    return e.sessions.some(s => s.is_bookable);
}

export function chEventListFilterFuture(e: CHEventList) {
    return e.sessions.length > 0;
}

export function chEventListFilterUpcoming(e: CHEventList) {
    return e.sessions.some(s => s.is_bookable); //TODOURGENT: || has live stream
}

export function chEventListInRange(e: CHEventList, from: YTime, to: YTime) {
    return e.sessions.some(s => $time(s.time).after(from) && $time(s.time).before(to));
}

export function chEventListMatchesQuery(e: CHEventList, query: string[]) {
    if(!query.length) return false;

    if(!e._search) {
        let search = ' ' + yGetI18N(e.name) + ' ' + e.venue_name;
        const now = $time.now();
        const we_start = $time.make(now.year, now.month, now.day_of_month - 1 + (7 - now.day_of_week) % 7);
        const we_end = we_start.add(2, $time.day);
        const today = now.formatIn('EEEE d MMMM', 'Europe/Stockholm');
        const tomorrow = now.add(1, $time.day).formatIn('EEEE d MMMM', 'Europe/Stockholm');
        for(const s of e.sessions) {
            if(!s.is_bookable) continue;
            const time = $time(s.time);
            const date = time.formatIn('EEEE d MMMM', 'Europe/Stockholm');
            search += ' ' + date;
            if(date === today)
                search += ' idag';
            if(date === tomorrow)
                search += ' imorgon';
            if(time.after(we_start) && time.before(we_end))
                search += ' helgen';
        }
        for(const t of e.event_tag_ids) {
            const tag = c_event_tags.maybe(t);
            if(tag)
                search += ' ' + cEventTagName(tag);
        }

        search += ' ';

        e._search = search.toLocaleLowerCase();
    }

    const search = e._search;

    for(const w of query) {
        if(!search.includes(w))
            return false;
    }

    return true;
}

function getHomeListView<Data = CHEventList[]>(sub_path: string) {
    return $api.cachedGET<Data>(`c/events/home_list_view/${sub_path}`);
}

export function chEventListFeaturedByCountry(country: YCountryId) {
    return getHomeListView(`featured/${country}`);
}

export function chEventListActiveByEventTag(event_tag_id: CEventTagId) {
    return getHomeListView(`active/by_event_tag/${event_tag_id}`);
}

export function chEventListActiveByCity(city_id: CCityId = abGetDefaultCity().id) {
    return getHomeListView(`active/by_city/${city_id}`);
}

export function chEventListPersonalized(subquery: CEventSubquery) {
    return getHomeListView(`personalized?type=${subquery}`);
}

interface CHEventListByVenueResponse {
    venue: CVenue;
    events: CHEventList[];
}

export function chEventListActiveByVenueSlug(city_id: CCityId, venue_slug: string) {
    return getHomeListView<CHEventListByVenueResponse>(`active/by_venue_slug/${city_id}/${venue_slug}`);
}

interface CHEventListByProducerResponse {
    producer_id: CProducerId;
    producer_name: string;
    events: CHEventList[];
}

export function chEventListActiveByProducerSlug(producer_slug: string) {
    return getHomeListView<CHEventListByProducerResponse>(`active/by_producer_slug/${producer_slug}`);
}

export function chEventListOnline(country: YCountryId) {
    return getHomeListView(`online/${country}`);
}

export function chEventListOnlineByEventTag(country: YCountryId, event_tag_id: CEventTagId) {
    return getHomeListView(`online/${country}/by_event_tag/${event_tag_id}`);
}

export function chEventListPast(city_id: CCityId, days: number) {
    return getHomeListView(`past/${city_id}/${days}`);
}

export function chEventListPastByYear(city_id: CCityId, year: string) {
    return getHomeListView(`past/${city_id}/by_year/${year}`);
}

export type CHEventCantBookReason = 'cancelled' | 'no_subs' | 'cancel_subs' | 'paused_subs' | 'sales_ended' | 'event_booking' | 'past_booking' | 'day_booking' | 'two_bookings' | 'no_tickets' | 'past_due' | 'loyalty' | 'loyalty_paused' | 'loyalty_inactive' | 'expired';

export interface CHEventSession {
    id: CSessionId;
    time: YTimeString;
    end_time?: YTimeString;
    sales_end: number;
    duration: number;
    is_bookable: boolean;
    is_drop_in: boolean;
    is_loyalty: boolean;
    ticket_type: CTicketType;
    instructions: YI18NString;
    price: YPaymentAmount;

    dots: number;
    is_booked: boolean;
    is_standby: boolean;
    can_book: boolean;
    max_book: number;
    max_book_free: number;
    can_standby: boolean;
    max_standby: number;
    reason?: CHEventCantBookReason;
}

export interface CHEventVideo {
    id: CVideoId;
    is_gone: boolean;
    is_cancelled: boolean;
    is_open: boolean;
    is_ppv: boolean;
    date?: YTimeString;
    expires?: YTimeString;
}

export interface CHEventVenue {
    id: CVenueId;
    name: string;
    slug: string;
    address: string;
    location: UCoordinates;
    map_zoom: number;
}

export interface CHEvent {
    id: CEventId;
    city_id: CCityId;
    venue: CHEventVenue;
    scene_name: string;
    producer_name: string;
    name: YI18NString;
    description: YI18NString;
    slug: string;
    event_tag_ids: CEventTagId[];
    keywords: string[];
    media_links: UMediaLink[];
    picture_ids: YId[];
    rating_score?: number;
    sessions: CHEventSession[];
    videos: CHEventVideo[];
    show_scene: boolean;
    days_until_loyalty: number;
}

export function chEventBySlug(city_id: CCityId, event_slug: string) {
    return $api.GET<CHEvent>(`c/event/home_view/by_slug/${city_id}/${event_slug}`);
}

export function chEventGenreTags(event: CHEvent) {
    const tags: CEventTag[] = [];

    for(const t of event.event_tag_ids) {
        const tag = c_event_tags.maybe(t);
        if(tag?.is_genre)
            tags.push(tag);
    }

    return tags;
}

export function chEventLocation(event: CHEventList | CHEvent) {
    return ['event', c_cities.maybe(event.city_id)!.slug, event.slug];
}

export function chEventPlayerLocation(event: CHEventList | CHEvent) {
    return ['event', c_cities.maybe(event.city_id)!.slug, event.slug, 'player'];
}

export function chEventUrl(event: CHEventList | CHEvent, country: YCountryId) {
    if(country == 'DE')
        return 'https://abundolive.de/' + chEventLocation(event).join('/');
    else
        return 'https://abundolive.se/' + chEventLocation(event).join('/');
}

export function chEventDurationText(event: CHEvent) {
    if(!event.sessions.length || event.sessions.some(s => s.is_drop_in))
        return;

    const duration = event.sessions[0].duration;
    if(!duration) return;

    const hours = Math.floor(duration / 60)
    const minutes = duration % 60;
    return (hours ? hours + ' ' + $i18n.tl.t_event_duration_h + ' ' : '') + (minutes ? minutes + ' ' + $i18n.tl.t_event_duration_m : '');
}

export function chEventTrackProperties(event: CHEvent) {
    const properties: { [index: string]: any } = { event_id: event.id, event_name: yGetI18N(event.name), event_keyword: event.keywords };
    const city = c_cities.maybe(event.city_id);
    if(city)
        properties.event_city = city.name;
    properties.venue = event.venue.name;
    properties.producer = event.producer_name;
    const genres = chEventGenreTags(event);
    if(genres.length)
        properties.genre = yGetI18N(genres[0].name);
    //TODO
    // if(model.videos && model.videos.length && model.videos[0].stream)
    //     properties.is_livestream = true;
    return properties;
}

export function chEventVideoIsAvailable(video: CHEventVideo) {
    if(video.is_gone || video.is_cancelled) return false;
    const expires = $time(video.expires);
    if(expires?.is_past) return false;

    return true;
}

export function chEventFilterPast(e: CHEvent | CHEventList) {
    const now = $time.now()
    return !e.sessions.some(s => $time(s.time).after(now));
}

export function chEventVenueDisplayName(event: CHEvent) {
    if(event.show_scene)
        return event.venue.name + ', ' + event.scene_name;
    return event.venue.name;
}

const tz = 'Europe/Stockholm';

export function chEventSessionPrettyTime(session: CHEventSession | CHBookingListSession, month_part: string = 'MMM yyyy') {
    const s = $time(session.time);
    if(!session.is_drop_in) return s.formatIn('EEE d MMM yyyy \u00B7 HH:mm', tz);
    const e = $time(session.end_time);
    if(!e)
        return s.formatIn('EEE d ' + month_part, tz);

    if(s.year !== e.year || s.month !== e.month || s.day_of_month !== e.day_of_month) {
        return s.format('d ' + month_part + ' \u00B7 HH:mm') + ' - ' + e.format('d MMM yyyy HH:mm');
    } else {
        return s.format('EEE d ' + month_part + ' \u00B7 HH:mm') + ' - ' + e.format('HH:mm');
    }
}

export function chEventSessionUnbookingEnd(session: CHEventSession) {
    return $time(session.time).add(-24, $time.hour);
}

export function chEventSessionSalesEnd(session: CHEventSession) {
    return $time(session.time).add(-session.sales_end, $time.hour);
}

